/**
 * Pure-function business logic for the Onboarding module.
 *
 * Covers both Tier 1 (Settings) and Tier 2 (Operational) validation rules.
 * Mirrors §7.1 Onboarding Business Rules in FUNCTIONAL_DOCUMENTATION.md.
 */

import { OnboardingStatus } from '../interfaces/IOnboardingFlow';
import {
  OnboardingDocumentType,
  IOnboardingSettings,
  IOnboardingDocument,
} from 'core/interfaces/onboardingInterface';

// ─── EIN validation ───────────────────────────────────────────────────────────

const EIN_REGEX = /^\d{2}-\d{7}$/;

/** Validates that an EIN matches the required XX-XXXXXXX format (e.g., 12-3456789). */
export function validateEIN(ein: string): boolean {
  return EIN_REGEX.test(ein);
}

// ─── Document template validation ─────────────────────────────────────────────

const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/** Validates a document file: must be PDF and ≤ 10 MB. */
export function validateDocumentFile(file: {
  type: string;
  size: number;
  name?: string;
}): string[] {
  const errors: string[] = [];
  const isPdf =
    file.type === 'application/pdf' ||
    (file.name?.toLowerCase().endsWith('.pdf') ?? false);
  if (!isPdf) {
    errors.push('Only PDF files are allowed.');
  }
  if (file.size > MAX_PDF_SIZE_BYTES) {
    errors.push('File size must not exceed 10 MB.');
  }
  return errors;
}

/** Checks that a document name is unique within the workplace document list. */
export function isDocumentNameUnique(
  name: string,
  existing: Array<Pick<IOnboardingDocument, 'id' | 'name'>>,
  excludeId?: number,
): boolean {
  return !existing.some(
    (d) =>
      d.name.trim().toLowerCase() === name.trim().toLowerCase() &&
      d.id !== excludeId,
  );
}

// ─── Required document helpers ────────────────────────────────────────────────

/**
 * Returns active document templates that have an uploaded PDF.
 * Templates without a file are excluded — the employee document step is skipped
 * when this list is empty.
 */
export function getRequiredOnboardingDocuments(
  documents: IOnboardingDocument[],
): IOnboardingDocument[] {
  return documents
    .filter((d) => d.active !== false && Boolean(d.filePath))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Keeps only phase-two document statuses that map to a required template. */
export function filterRequiredDocumentStatuses<T extends { template_id: number }>(
  statuses: T[],
  templates: IOnboardingDocument[],
): T[] {
  const requiredTemplateIds = new Set(
    getRequiredOnboardingDocuments(templates)
      .map((t) => t.id)
      .filter((id): id is number => id !== undefined),
  );
  return statuses.filter((status) => requiredTemplateIds.has(status.template_id));
}

// ─── Settings prerequisite check ─────────────────────────────────────────────

/**
 * Returns true only when onboarding settings are complete enough to start
 * accepting new onboarding records:
 *   - valid EIN (XX-XXXXXXX)
 *   - storage provider selected
 *   - at least one active document template
 */
export function isOnboardingSettingsReady(
  settings: Partial<IOnboardingSettings> | null | undefined,
  documents: IOnboardingDocument[],
): boolean {
  if (!settings) return false;
  if (!settings.ein || !validateEIN(settings.ein)) return false;
  if (!settings.storageProvider) return false;
  const hasActiveDocument = documents.some((d) => d.active !== false);
  return hasActiveDocument;
}

// ─── Status transition rules ──────────────────────────────────────────────────

const ALLOWED_TRANSITIONS: Partial<Record<OnboardingStatus, OnboardingStatus[]>> = {
  [OnboardingStatus.DRAFT]: [
    OnboardingStatus.EMPLOYEE_PENDING,
    OnboardingStatus.ARCHIVED,
  ],
  [OnboardingStatus.EMPLOYEE_PENDING]: [
    OnboardingStatus.MANAGER_REVIEW,
    OnboardingStatus.ARCHIVED,
  ],
  [OnboardingStatus.MANAGER_REVIEW]: [
    OnboardingStatus.COMPLETED,
    OnboardingStatus.EMPLOYEE_PENDING, // request-changes path
    OnboardingStatus.ARCHIVED,
  ],
  [OnboardingStatus.COMPLETED]: [OnboardingStatus.ARCHIVED],
  [OnboardingStatus.ARCHIVED]: [],
  [OnboardingStatus.IN_PROGRESS]: [
    OnboardingStatus.MANAGER_REVIEW,
    OnboardingStatus.ARCHIVED,
  ],
};

/** Returns whether transitioning from `from` to `to` is a valid status change. */
export function canTransitionStatus(
  from: OnboardingStatus,
  to: OnboardingStatus,
): boolean {
  return (ALLOWED_TRANSITIONS[from] ?? []).includes(to);
}

// ─── Document type helpers ────────────────────────────────────────────────────

/** Returns a human-readable label for a document type code. */
export function getDocumentTypeLabel(code: OnboardingDocumentType): string {
  switch (code) {
    case OnboardingDocumentType.DATA_ENTRY:
      return 'Interactive Form (Data Entry)';
    case OnboardingDocumentType.UPLOAD:
      return 'Employee Upload';
    case OnboardingDocumentType.POLICY:
      return 'Policy Acknowledgment';
    default:
      return 'Unknown';
  }
}

/**
 * Returns true when the Phase 3 attestation modal must be shown for a document.
 * Driven by the isManagerSignatureRequired flag on the template.
 */
export function isManagerAttestationRequired(
  doc: Pick<IOnboardingDocument, 'isManagerSignatureRequired'>,
): boolean {
  return doc.isManagerSignatureRequired === true;
}

// ─── Manager initiation validation ───────────────────────────────────────────

export interface ManagerInitiationInput {
  email: string;
  firstName: string;
  lastName: string;
  hierarchyAssignment: string;
  employmentStartDate: string;
}

/** Validates the Phase 1 manager initiation form. Returns error messages. */
export function validateManagerInitiation(
  data: ManagerInitiationInput,
): string[] {
  const errors: string[] = [];
  if (!data.firstName?.trim()) errors.push('First name is required.');
  if (!data.lastName?.trim()) errors.push('Last name is required.');
  if (
    !data.email?.trim() ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())
  ) {
    errors.push('A valid email address is required.');
  }
  if (!data.hierarchyAssignment?.trim()) {
    errors.push('Hierarchy assignment is required.');
  }
  if (!data.employmentStartDate?.trim()) {
    errors.push('Employment start date is required.');
  }
  return errors;
}
